import { HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTutorialMode } from "@/contexts/tutorial-mode";
import { getTutorialHint } from "@/lib/tutorialHints";
import { cn } from "@/lib/utils";

interface TutorialHintProps {
  id: string;
  className?: string;
  /** Override the size of the icon (default 14px). */
  size?: number;
  /** Optional inline label rendered next to the icon. */
  label?: string;
}

/**
 * A small "?" popover that explains a feature when tutorial mode is on.
 * Renders nothing when the user has tutorial hints disabled or when the
 * id has no entry in the registry.
 */
export function TutorialHint({ id, className, size = 14, label }: TutorialHintProps) {
  const { enabled } = useTutorialMode();
  const hint = getTutorialHint(id);
  if (!enabled || !hint) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Help: ${hint.title}`}
          // Stop click bubbling so the surrounding button/link/menu-item
          // doesn't navigate, but DO NOT preventDefault — Radix needs the
          // default click to flow through in order to toggle the popover.
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={cn(
            "inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors align-middle",
            className,
          )}
          data-testid={`tutorial-hint-${id}`}
        >
          <HelpCircle style={{ width: size, height: size }} />
          {label ? <span className="text-xs">{label}</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-72 text-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid={`tutorial-hint-content-${id}`}
      >
        <div className="font-semibold mb-1">{hint.title}</div>
        <p className="text-muted-foreground leading-snug">{hint.body}</p>
      </PopoverContent>
    </Popover>
  );
}
